import {
  get,
  isBoolean,
  isNumber,
  map,
  reduce,
  toString,
  isEmpty as isLodashEmpty,
  pick,
  forEach,
  find,
  gt,
  every,
  some,
  omit,
  findIndex,
  replace,
  cloneDeep,
  set,
  trim
} from 'lodash';
import { BenefitTypes, ProductContent } from '../global';
import {
  Benefit,
  EligibilityOptsType,
  ProductBenefitConfig,
  ProductConfig,
  RecommendationPlanResponse,
  RecommendationResponsePayload,
  RecommendationState,
  RecommendationStoreResponse,
  TripCost
} from './types';

const configsConfig='configs[0].config'

const organizationContractProductConfigs = 'organizationContract.productConfigs';

const planContentFields = [
  'plan.code',
  'plan.content.code',
  'plan.content.details',
  'plan.content.eligibility',
  'plan.content.lang',
  'plan.content.name',
  'plan.content.pdfLink',
  'plan.content.sumInsuredAfterNeeded',
  'plan.content.summary',
  'plan.planClass',
  'plan.planType',
  'salesChannel'
];

const benefitContentFields = [
  'benefitType',
  'code',
  'content.code',
  'content.details',
  'content.lang',
  'content.name',
  'content.sumInsuredAfterNeeded',
  'content.summary'
];

const planRecommendationFields = [
  'code',
  'isMQNeeded',
  'planClass',
  'purchasable',
  'status',
  'travellerType',
  'tripCostConfig',
  'tripType'
];

const benefitRecommendationFields = ['benefitType', 'code', 'planCode'];

export const isEmpty = (value: unknown): boolean => !isBoolean(value) && !isNumber(value) && isLodashEmpty(value);

export const isIncomplete = (content: unknown, fields: string[]): boolean => {
  return some(fields, field => {
    const value = get(content, field);
    if (field === 'tripCostConfig' && isTripCostInvalid(value)) {
      return true;
    }
    return isEmpty(value);
  });
};

export const mapRecommendationResponseToPax = (act: RecommendationResponsePayload[]): RecommendationState => {
  const paxes = convertIndividualRecommendation(
    map(act, rec => ({
      recommendationData: rec,
      loading: false,
      referenceNumber: toString(rec.referenceNumber)
    }))
  ) as { [props: number]: RecommendationStoreResponse };
  const recommendationError = some(paxes, pax => pax.isAllPlansError);
  return { ...paxes, recommendationError };
};

export const convertIndividualRecommendation = (act: RecommendationStoreResponse[]): RecommendationState =>
  reduce(
    act,
    (res, paxRef) => {
      const validatedPlans = validatePlans(paxRef);
      return {
        ...res,
        [paxRef.referenceNumber]: { ...paxRef, ...validatedPlans, loading: false }
      };
    },
    {}
  );

export const getRecommendationError = (recommendation: RecommendationResponsePayload[]): boolean => {
  return some(recommendation, travellerRecommendation => {
    const plans = get(travellerRecommendation, 'plans');
    const isPlansEmpty = isEmpty(plans);
    const isPlansError = !isEmpty(get(travellerRecommendation, 'error'));
    return isPlansEmpty || isPlansError;
  });
};

export const getRecommendationWithContentError = (
  recommendation: RecommendationState,
  content: ProductContent[]
): boolean => {
  const recommendationError = get(recommendation, 'recommendationError');
  if (isEmpty(recommendation) || isEmpty(content) || recommendationError) {
    return false;
  }
  return some(recommendation, pax => {
    const plans = get(pax, 'recommendationData.plans');
    return (
      !isEmpty(plans) &&
      every(plans, plan => {
        // @ts-ignore
        const planContent = find(content, { code: plan.code });
        return isEmpty(planContent) || get(planContent, 'hasError') || get(plan, 'hasError');
      })
    );
  });
};

export const isTripCostInvalid = (tripCostConfig: TripCost): boolean => {
  if (isEmpty(tripCostConfig)) {
    return true;
  }
  const { eligible, maxValue } = tripCostConfig;
  if (eligible && !gt(maxValue, 0)) {
    return true;
  }
  return false;
};

export const validatePlans = (recommendation: RecommendationStoreResponse): RecommendationStoreResponse => {
  const plans = get(recommendation, 'recommendationData.plans', []);
  const { plans: validatedPlans, isAllPlansError } = reduce(
    plans,
    (acc, plan) => {
      const hasError = isIncomplete(plan, planRecommendationFields);
      const benefits = validateBenefits(plan);
      return {
        plans: [...acc.plans, { ...plan, ...benefits, hasError }],
        isAllPlansError: acc.isAllPlansError && hasError
      };
    },
    { plans: [], isAllPlansError: true }
  );
  return { recommendationData: { ...recommendation.recommendationData, plans: validatedPlans }, isAllPlansError };
};

export const validateBenefits = (
  plan: RecommendationPlanResponse
): { benefits: Benefit[]; isAllBenefitsError: boolean } => {
  const benefits = get(plan, 'benefits');
  const { benefits: validatedBenefits, isAllBenefitsError } = reduce(
    benefits,
    (acc, benefit) => {
      const isMandatory = get(benefit, 'benefitType') === BenefitTypes.MANDATORY;
      const hasError = isMandatory ? true : isIncomplete(benefit, benefitRecommendationFields);
      return {
        benefits: [...acc.benefits, { ...benefit, hasError }],
        isAllBenefitsError: acc.isAllBenefitsError && hasError
      };
    },
    { benefits: [], isAllBenefitsError: true }
  );
  return { benefits: validatedBenefits, isAllBenefitsError };
};

export const mapContent = (
  content: {
    organizationContract: { productConfigs: { plan: { content: ProductContent } }[] };
  },
  contextPath?: string
): ProductContent[] => {
  return reduce(
    get(content, organizationContractProductConfigs),
    (res, val) => {
      const updatedPlan = updatePlanConfigs(contextPath, val);
      const hasPlanError = isIncomplete(updatedPlan, planContentFields);
      res.push({
        ...get(updatedPlan.plan, 'content', {}),
        ...pick(updatedPlan.plan, ['planType', 'planClass']),
        hasError: hasPlanError
      });
      // @ts-ignore
      forEach(val.plan.benefits, ben => {
        const benefitConfig = get(ben, configsConfig, {});
        const hasBenefitError = ben.benefitType === BenefitTypes.OPTIONAL && isIncomplete(ben, benefitContentFields);        
        const eligibilityOpts = get(benefitConfig, 'eligibilityOpts', []);
        const tripCostEligibilityOps = find(eligibilityOpts, opt => opt.type === EligibilityOptsType.TRIP_COST);
        const tripCostConfig: TripCost = tripCostEligibilityOps
          ? {
              eligible: true,
              ...omit(tripCostEligibilityOps, 'type')
            }
          : {
              eligible: false
            };
        return res.push({
          ...get(ben, 'content', {}),
          ...pick(ben, ['benefitType', 'code']),
          tripCostConfig,          
          hasError: hasBenefitError,
          rules: get(benefitConfig, 'rules', [])
        });
      });
      return res;
    },
    []
  );
};

export const mapApiContent = (
content: {
  productConfigs: { plan: { content: ProductContent } }[] ;
  },
  contextPath?: string
): ProductContent[] => {
  return reduce(
    get(content, 'productConfigs'),
    (res, val) => {
      const updatedPlan = updatePlanConfigs(contextPath, val);
      const hasPlanError = isIncomplete(updatedPlan, planContentFields);
      res.push({
        ...get(updatedPlan.plan, 'content', {}),
        ...pick(updatedPlan.plan, ['planType', 'planClass']),
        hasError: hasPlanError
      });
      // @ts-ignore
      forEach(val.plan.benefits, ben => {
        const hasBenefitError = ben.benefitType === BenefitTypes.OPTIONAL && isIncomplete(ben, benefitContentFields);
        const benefitConfig = get(ben, configsConfig, {});
        const eligibilityOpts = get(benefitConfig, 'eligibilityOpts', []);
        const tripCostEligibilityOps = find(eligibilityOpts, opt => opt.type === EligibilityOptsType.TRIP_COST);
        const tripCostConfig: TripCost = tripCostEligibilityOps
          ? {
              eligible: true,
              ...omit(tripCostEligibilityOps, 'type')
            }
          : {
              eligible: false
            };
        return res.push({
          tripCostConfig,
          ...get(ben, 'content', {}),
          ...pick(ben, ['benefitType', 'code']),          
          rules: get(benefitConfig, 'rules', []),
          hasError: hasBenefitError
        });
      });
      return res;
    },
    []
  );
};

export const mapProductConfig = (productConfig: {
  organizationContract: { productConfigs: { plan: { configs: { config: ProductConfig[] } } }[] };
}): ProductContent[] => {
  return reduce(
    get(productConfig, organizationContractProductConfigs),
    (res, val) => {
      res.push({
        code: get(val.plan, 'code'),
        ...get(val.plan, configsConfig, {})
      });
      return res;
    },
    []
  );
};

export const mapProductBenefitConfig = (productConfig: {
  organizationContract: { productConfigs: { plan: { benefits: { configs: { config: ProductBenefitConfig[] } } } }[] };
}): ProductContent[] => {
  return reduce(
    get(productConfig, organizationContractProductConfigs),
    (res, val) => {
      // @ts-ignore
      const benefitCode = `${val.plan.code}_BO`;
      // @ts-ignore
      const benefitIndex = findIndex(val.plan.benefits, { code: benefitCode });
      res.push({
        code: get(val.plan, 'code'),
        ...get(val.plan, `benefits[${benefitIndex}].configs[0].config`, {})
      });
      return res;
    },
    []
  );
};

export const updatePlanConfigs = (
  contextPath: string,
  data: { plan: { content: ProductContent } }
): { plan: { content: ProductContent } } => {
  const updatedContextPath = trim(contextPath);
  if (!isEmpty(updatedContextPath)) {
    const fieldsToBeReplaced = [
      {
        fieldName: 'plan.content.details',
        pattern: /(href="\/content)/g,
        replacement: `href="${contextPath}/content`
      },
      {
        fieldName: 'plan.content.pdfLink.cmProductPdfLink',
        pattern: /^(\/content)/,
        replacement: `${contextPath}/content`
      }
    ];
    const dataClone = cloneDeep(data);

    forEach(fieldsToBeReplaced, field => {
      const updatedDetails = replace(get(dataClone, field.fieldName), field.pattern, field.replacement);
      set(dataClone, field.fieldName, updatedDetails);
    });
    return dataClone;
  }
  return data;
};
